import { useCallback, useEffect, useState } from "react";
import {
  ErrorText,
  FieldLabel,
  LoadingView,
  PrimaryButton,
  SBCard,
  SecondaryButton,
} from "@/components/ui";
import {
  espnAccessRequestTargetDisplayName,
  type ESPNAccessRequest,
  type ESPNAccessRequestDecision,
} from "@/models/espnAccessRequest";
import {
  ESPN_ACCESS_DENY_REASON_MAX_LENGTH,
  decideAdminESPNAccessRequest,
  listAdminESPNAccessRequests,
} from "@/services/espnAccessRequests";
import "@/features/home/home.css";
import "./espnAccessRequests.css";

export function ESPNAccessRequestAdminView() {
  const [requests, setRequests] = useState<ESPNAccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setRequests(await listAdminESPNAccessRequests());
    } catch (cause) {
      setError(errorMessage(cause, "ESPN access requests could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceRequest = (request: ESPNAccessRequest) => {
    setRequests((current) =>
      [request, ...current.filter((candidate) => candidate.id !== request.id)]
        .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime()));
  };

  const pending = requests.filter((request) => request.status === "pending");
  const decided = requests.filter((request) => request.status !== "pending");

  return (
    <div className="list-screen espn-access-screen">
      <div className="list-screen-head">
        <div>
          <p className="home-eyebrow">SBot Labs Admin</p>
          <h1>ESPN Access</h1>
        </div>
        <SecondaryButton title="Refresh" onClick={() => void load()} disabled={isLoading} />
      </div>

      <SBCard className="home-card espn-access-intro">
        <h3>ESPN Access Requests</h3>
        <p className="home-hint">
          Approve or deny requests to start from ESPN leagues and teams.
          Approval unlocks the entry in onboarding for that user.
        </p>
        <ErrorText>{error}</ErrorText>
      </SBCard>

      {isLoading && requests.length === 0 ? (
        <SBCard><LoadingView label="Loading requests…" /></SBCard>
      ) : requests.length === 0 ? (
        <SBCard className="home-card">
          <h3>No requests</h3>
          <p className="home-hint">New ESPN access requests will appear here and by email.</p>
        </SBCard>
      ) : (
        <>
          <ESPNAccessRequestSection
            title="Pending"
            requests={pending}
            onUpdated={replaceRequest}
          />
          <ESPNAccessRequestSection
            title="Decided"
            requests={decided}
            onUpdated={replaceRequest}
          />
        </>
      )}
    </div>
  );
}

/**
 * Inline request summary and approve/deny controls for a submitted-request
 * notification. The admin Functions endpoints remain the only data source.
 */
export function ESPNAccessRequestActionSection({ requestID }: { requestID: string }) {
  const [request, setRequest] = useState<ESPNAccessRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const requests = await listAdminESPNAccessRequests();
      const match = requests.find((candidate) => candidate.id === requestID) ?? null;
      setRequest(match);
      if (!match) setError("This access request no longer exists.");
    } catch (cause) {
      setError(errorMessage(cause, "This access request could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, [requestID]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <SBCard className="home-card espn-access-inline">
      <h3>ESPN Access Request</h3>
      {isLoading ? (
        <LoadingView label="Loading request…" />
      ) : request ? (
        <ESPNAccessRequestCard request={request} onUpdated={setRequest} compact />
      ) : (
        <p className="home-hint">{error ?? "This access request could not be loaded."}</p>
      )}
      {request && <ErrorText>{error}</ErrorText>}
    </SBCard>
  );
}

function ESPNAccessRequestSection({
  title,
  requests,
  onUpdated,
}: {
  title: string;
  requests: ESPNAccessRequest[];
  onUpdated: (request: ESPNAccessRequest) => void;
}) {
  if (requests.length === 0) return null;
  return (
    <section className="espn-access-section">
      <h2>{title}</h2>
      <div className="espn-access-list">
        {requests.map((request) => (
          <SBCard className="home-card" key={request.id}>
            <ESPNAccessRequestCard request={request} onUpdated={onUpdated} />
          </SBCard>
        ))}
      </div>
    </section>
  );
}

function ESPNAccessRequestCard({
  request,
  onUpdated,
  compact = false,
}: {
  request: ESPNAccessRequest;
  onUpdated: (request: ESPNAccessRequest) => void;
  compact?: boolean;
}) {
  const [denyReason, setDenyReason] = useState("");
  const [isDeciding, setIsDeciding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = espnAccessRequestTargetDisplayName(request);

  const decide = async (decision: ESPNAccessRequestDecision) => {
    const action = decision === "approve" ? "Approve" : "Deny";
    if (!window.confirm(
      decision === "approve"
        ? `${action} ${request.name}'s access to ${target}?`
        : `${action} ${request.name}'s request for ${target}?`,
    )) return;

    setIsDeciding(true);
    setError(null);
    try {
      const updated = await decideAdminESPNAccessRequest(
        request.id,
        decision,
        decision === "deny" ? denyReason : undefined,
      );
      onUpdated(updated);
      setDenyReason("");
    } catch (cause) {
      setError(errorMessage(cause, "The access decision could not be saved."));
    } finally {
      setIsDeciding(false);
    }
  };

  return (
    <div className={`espn-access-request${compact ? " compact" : ""}`}>
      <div className="espn-access-request-head">
        <div>
          <strong>{target}</strong>
          <span>
            {request.targetType === "team"
              ? `Team template in ${request.leagueName}`
              : "League"}
            {" · "}{request.sportID}
          </span>
        </div>
        <span className={`badge espn-status-${request.status}`}>
          {statusLabel(request.status)}
        </span>
      </div>

      <dl className="espn-access-summary">
        <div><dt>Requester</dt><dd>{request.name}</dd></div>
        <div><dt>Email</dt><dd>{request.email}</dd></div>
        {!compact && <div><dt>Phone</dt><dd>{request.phone}</dd></div>}
        {!compact && (
          <div>
            <dt>Updated</dt>
            <dd>{request.updatedAt.toLocaleString()}</dd>
          </div>
        )}
      </dl>

      {!compact && <p className="espn-access-message">{request.message}</p>}

      {request.status === "pending" ? (
        <div className="espn-access-decision">
          <FieldLabel label="Deny reason (optional)">
            <textarea
              value={denyReason}
              onChange={(event) => setDenyReason(event.target.value)}
              maxLength={ESPN_ACCESS_DENY_REASON_MAX_LENGTH}
              rows={compact ? 2 : 3}
            />
          </FieldLabel>
          <div className="espn-access-actions">
            <SecondaryButton
              title="Deny"
              destructive
              disabled={isDeciding}
              onClick={() => void decide("deny")}
            />
            <PrimaryButton
              title="Approve"
              disabled={isDeciding}
              onClick={() => void decide("approve")}
            />
          </div>
        </div>
      ) : (
        <div className="espn-access-decision-summary">
          {request.decidedBy && <span>Decided by {request.decidedBy}</span>}
          {request.denyReason && <span>Reason: {request.denyReason}</span>}
        </div>
      )}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

function statusLabel(status: ESPNAccessRequest["status"]): string {
  return status[0]!.toUpperCase() + status.slice(1);
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}
